import moment from "moment";
import { AdjustmentValidation } from "../../../MealTrack/MTAPITypes";
export const NO_SUBTOTAL_FOUND = `Next order's subtotal not found.`;
export const NO_TOTAL_FOUND = `Next order's total not found.`;
/**
 * Gets the next order subtotal display value.
 * @param balance
 * @param settings
 * @param perMealCouponDiscountAppliedValue
 * @param useCurrencyLocalizer
 * @returns
 */
export const getNextOrderSubtotalDisplay = (balance, settings, perMealCouponDiscountAppliedValue, useCurrencyLocalizer) => {
    var _a;
    const nextOrderSubtotal = (_a = balance === null || balance === void 0 ? void 0 : balance.cost.subscription) === null || _a === void 0 ? void 0 : _a.subtotal;
    const subtotalWithDiscount = (nextOrderSubtotal !== null && nextOrderSubtotal !== void 0 ? nextOrderSubtotal : 0) - ((settings === null || settings === void 0 ? void 0 : settings.discountPerMeal.display) ? perMealCouponDiscountAppliedValue : 0);
    const { localizedCurrencyText: nextOrderSubtotalCurrencyText } = useCurrencyLocalizer(subtotalWithDiscount);
    return {
        nextOrderSubtotal,
        nextOrderSubtotalCurrencyText: nextOrderSubtotal ? nextOrderSubtotalCurrencyText : NO_SUBTOTAL_FOUND,
    };
};
/**
 * * Gets the total cost for a given day from the balance object.
 * @param day
 * @param balance
 * @param settings
 * @param isInvoiceModal
 * @param perMealDiscount
 * @returns
 */
export const getDayPrice = (day, balance, settings, isInvoiceModal = false, perMealDiscount) => {
    var _a, _b, _c, _d;
    // Extract distributions from balance.
    const distributions = (_a = balance === null || balance === void 0 ? void 0 : balance.cost.order) === null || _a === void 0 ? void 0 : _a.distributions;
    // Flatten day groups into a single array.
    let flattenedDayGroups = distributions === null || distributions === void 0 ? void 0 : distributions.flatMap((d) => {
        return d.dayGroups;
    });
    // Calculate daily costs from flattenedDayGroups
    const dailyCosts = {};
    // Flag to indicate if a matching distribution has been found.
    let foundMatchingDayGroup = false;
    flattenedDayGroups === null || flattenedDayGroups === void 0 ? void 0 : flattenedDayGroups.some((dist, index) => {
        if (foundMatchingDayGroup) {
            return true; // Exit the loop if a matching distribution has been found
        }
        const dayDate = moment(dist.day);
        const dayOfWeek = dayDate.format("dddd"); // e.g., "Monday"
        const totalCost = settings.discountPerMeal.display ? dist.discountedCost : dist.totalCost;
        // Create a key based on the day of the week
        const dayKey = `${dayOfWeek}`;
        // Add or accumulate the total cost for the day
        if (dailyCosts[dayKey]) {
            dailyCosts[dayKey] += totalCost;
        }
        else {
            dailyCosts[dayKey] = totalCost;
        }
        // Check if the distribution matches the day
        const mealDate = moment(day.date);
        const mealDayOfWeek = mealDate.format("dddd"); // e.g., "Monday"
        if (dayOfWeek === mealDayOfWeek) {
            foundMatchingDayGroup = true; // Set flag to true if a matching distribution is found
            flattenedDayGroups === null || flattenedDayGroups === void 0 ? void 0 : flattenedDayGroups.splice(index, 1); // Remove the used distribution
        }
        return false; // Continue to the next distribution
    });
    // Get the total cost for the meal day
    const mealDate = moment(day.date);
    const mealDayOfWeek = mealDate.format("dddd"); // e.g., "Monday"
    const totalCostForMealDay = dailyCosts[mealDayOfWeek] || 0;
    const productCost = (_c = (_b = distributions === null || distributions === void 0 ? void 0 : distributions.find((d) => { var _a; return (_a = moment(d.day)) === null || _a === void 0 ? void 0 : _a.isSame(mealDate, "day"); })) === null || _b === void 0 ? void 0 : _b.productsCost) !== null && _c !== void 0 ? _c : 0;
    // Get the discount for the day
    const dayDiscount = settings.discountPerMeal.display
        ? ((_d = day.meals) === null || _d === void 0 ? void 0 : _d.reduce((acc, m) => acc + m.quantity, 0)) * perMealDiscount
        : 0;
    const dayPrice = (totalCostForMealDay - dayDiscount) + productCost;
    return { dayPrice };
};
/**
 * * Gets the total cost for a given meal from the balance object.
 * @param mealDate
 * @param programMealId
 * @param balance
 * @returns
 */
export const getMealPrice = (mealDate, programMealId, balance) => {
    const dayDistribution = balance === null || balance === void 0 ? void 0 : balance.cost.order.distributions.find((d) => moment(d.day).isSame(mealDate, "day"));
    const dayGroup = dayDistribution === null || dayDistribution === void 0 ? void 0 : dayDistribution.dayGroups.find((d) => moment(d.day).isSame(mealDate, "day"));
    const mealFromBalance = dayGroup === null || dayGroup === void 0 ? void 0 : dayGroup.meals.find((m) => m.programMealId === programMealId);
    return mealFromBalance === null || mealFromBalance === void 0 ? void 0 : mealFromBalance.totalCost;
};
/**
 * Compares the meals to the subscription template to check if they match in terms of days and quantities
 *
 * @param meals
 * @param subscriptionMeals
 * @returns
 */
const compareMealsAndTemplate = (meals, subscriptionMeals) => {
    // Create maps to store quantities of meals and subscription meals by their respective days of the week
    const mealDays = new Map();
    const subscriptionMealDays = new Map();
    meals.forEach((meal) => {
        const dayOfWeek = moment(meal.MealDate).day();
        mealDays.set(dayOfWeek, meal.Quantity);
    });
    subscriptionMeals.forEach((cartSubscriptionMeal) => {
        subscriptionMealDays.set(cartSubscriptionMeal.DayOfWeek, cartSubscriptionMeal.Qty);
    });
    // If the number of unique days in meals and subscription meals are different, they don't match
    if (mealDays.size !== subscriptionMealDays.size) {
        return false;
    }
    // Check if each day in mealDays map has the same quantity as in subscriptionMealDays map
    for (const [day, quantity] of mealDays) {
        if (subscriptionMealDays.get(day) !== quantity) {
            return false;
        }
    }
    return true;
};
/**
 * Checks if the order contains a short week by comparing the meals to the subscription template
 *
 * @param meals
 * @param subscriptionMeals
 * @returns
 */
export const doesOrderContainShortWeek = (meals, subscriptionMeals) => {
    const mealsByWeek = groupMealsByWeek(meals);
    const firstWeekOfMeals = getFirstWeekOfMeals(mealsByWeek);
    if (!(firstWeekOfMeals === null || firstWeekOfMeals === void 0 ? void 0 : firstWeekOfMeals.meals))
        return false;
    // The template represents meals for a single week, so we only need to compare the first week's meals
    // to the template. Comparing all meals in the cart would be incorrect because they are repeated
    // across the subscription period.
    const doMealsMatchTemplate = compareMealsAndTemplate(firstWeekOfMeals.meals, subscriptionMeals);
    // If the meals do not match the subscription template, it indicates a short week
    return !doMealsMatchTemplate;
};
/**
 * Groups an array of meals by the starting date of the week in which each meal occurs.
 *
 * @param {Meal[]} meals - The array of meals to be grouped by week.
 * @returns {Record<string, Meal[]>} - An object where each key is the starting date of a week (in "YYYY-MM-DD" format)
 *                                     and the corresponding value is an array of meals that occur in that week.
 */
const groupMealsByWeek = (meals) => {
    return meals.reduce((acc, meal) => {
        const weekStart = moment(meal.MealDate).startOf("week").format("YYYY-MM-DD");
        if (!acc[weekStart]) {
            acc[weekStart] = [];
        }
        acc[weekStart].push(meal);
        return acc;
    }, {});
};
/**
 * Retrieves the first week of meals from a grouped meals-by-week object.
 *
 * @param {Record<string, Meal[]>} mealsByWeek - An object where each key is the starting date of a week
 *                                               and the corresponding value is an array of meals for that week.
 * @returns {{ weekStartDate: string; meals: Meal[] } | null} - An object containing the start date of the first week
 *                                                              and the meals for that week, or null if there are no meals.
 */
const getFirstWeekOfMeals = (mealsByWeek) => {
    const firstWeekEntry = Object.entries(mealsByWeek)[0];
    if (firstWeekEntry) {
        const [weekStartDate, meals] = firstWeekEntry;
        return { weekStartDate, meals };
    }
    return null; // Return null if mealsByWeek is empty or has no entries
};
/**
 * This function filters adjustments based on their validity and formats them for use in the order summary.
 *
 * @param {Balance | undefined} balance - The balance object
 * @returns {OrderSummaryDiscount[] | undefined} - An array of valid adjustments formatted for the order summary,
 * or `undefined` if no valid adjustments are found.
 */
export const getOrderAdjustments = (balance) => {
    return balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustments.filter((adjustment) => adjustment.validation === AdjustmentValidation.Valid).map((adjustment) => {
        return {
            name: adjustment.name,
            type: adjustment.application,
            value: adjustment.adjustmentValue,
            appliedValue: adjustment.appliedValue,
            adjustmentType: adjustment.adjustmentType,
            validationMsg: adjustment.validation,
            automaticDiscount: adjustment.automatic,
        };
    });
};
